import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";

import {
  FiPackage,
  FiLogOut,
  FiUser,
  FiBarChart,
  FiMenu,
  FiSettings,
} from "react-icons/fi";
import { BiSupport } from "react-icons/bi";

import {
  LoadingContainer,
  SidebarNav,
  SidebarSubtitle,
  SidebarHeader,
  Sidebar,
  SidebarToggle,
  NavItem,
  NavIcon,
  NavText,
  BottomNav,
  Container,
  NavSupport,
  NavLogout,
  Header,
  HeaderActions,
  UserInfo,
  MainContent,
  AlertDot,
  Content,
  LogoIcon,
  SidebarOverlay,
} from "./BandLayout.styles";
import EmailVerificationModal from "../../../components/utils/EmailVerificationModal";
import VerificationStatusBadge, {
  getVerificationStatus,
} from "../../../components/utils/VerificationStatusBadge";
import SupportChat from "../../../components/SupportChat/SupportChat";
import { getVerificationStatus as getVerificationStatusAsync } from "../../../store/slices/emailVerification/asyncThunks";
import {
  selectEmailVerificationData,
  selectEmailVerificationStatus,
} from "../../../store/slices/emailVerification/slice";
import { logout } from "../../../store/slices/auth/asyncThunks";
import { useLocation, useNavigate } from "react-router-dom";
import { selectUser } from "../../../store/slices/auth/slice";
import {
  selectBandProducts,
  selectBandOrders,
} from "../../../store/slices/bandDashboard/slice";
import { toast } from "../../../components/modern-ui/sonner";
import { TbTruckDelivery } from "react-icons/tb";
import { IoClose } from "react-icons/io5";

const navItems = [
  {
    id: "dashboard",
    label: "Kontrolna Tabla",
    icon: FiBarChart,
    to: "/bend-panel/kontrolna-tabla",
  },
  {
    id: "products",
    label: "Proizvodi",
    icon: FiPackage,
    to: "/bend-panel/proizvodi",
  },
  {
    id: "orders",
    label: "Porudzbine",
    icon: TbTruckDelivery,
    to: "/bend-panel/porudzbine",
  },
  {
    id: "settings",
    label: "Podešavanja",
    icon: FiSettings,
    to: "/bend-panel/podesavanja",
  },
];

const BandLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector(selectUser);
  const bandProducts = useSelector(selectBandProducts);
  const bandOrders = useSelector(selectBandOrders);

  const [emailVerificationModal, setEmailVerificationModal] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [supportChatMinimized, setSupportChatMinimized] = useState(false);
  const verificationData = useSelector(selectEmailVerificationData);
  const verificationStatus = useSelector(selectEmailVerificationStatus);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hideContent, setHideContent] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  useEffect(() => {
    if (sidebarCollapsed) {
      setTimeout(() => {
        setHideContent(true);
      }, 250);
    } else {
      setHideContent(false);
    }
  }, [sidebarCollapsed]);

  const changePage = (page) => {
    navigate(page);
    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      setSidebarCollapsed(true);
    }

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        toast.info("Uspešno ste izlogovali sa naloga.");
      })
      .catch((err) => console.log(err.message));
  };

  const handleOpenVerificationModal = () => {
    setEmailVerificationModal(true);
  };

  // Check for low stock products and pending orders
  const hasLowStockAlerts =
    bandProducts?.data?.data?.some((product) =>
      product.productSizes?.some((size) => size.stockSize <= size.stockAlert)
    ) || false;

  const hasPendingOrders =
    bandOrders?.data?.some((order) => order.status === "pending") || false;

  // Support chat handlers
  const handleOpenSupportChat = () => {
    setSupportChatOpen(true);
    setSupportChatMinimized(false);
  };

  const handleCloseSupportChat = () => {
    setSupportChatOpen(false);
    setSupportChatMinimized(false);
  };

  const handleMinimizeSupportChat = () => {
    setSupportChatMinimized(!supportChatMinimized);
  };

  // Email verification functions
  const checkVerificationStatus = () => {
    if (!user || user.role !== "band") return;
    dispatch(getVerificationStatusAsync());
  };

  const handleCloseVerificationModal = () => {
    setEmailVerificationModal(false);
    // Recheck verification status after modal closes
    setTimeout(() => {
      checkVerificationStatus();
    }, 1000);
  };

  useScrollLock(emailVerificationModal);

  // Check email verification status for band users
  useEffect(() => {
    if (user && user.role === "band") {
      dispatch(getVerificationStatusAsync())
        .unwrap()
        .then((res) => {
          if (!res.emailVerified) {
            setEmailVerificationModal(true);
          }
        });
    }
  }, []);

  if (!user || user.role !== "band") {
    return (
      <LoadingContainer>
        <l-line-spinner size="32" stroke="2.5" speed="1" color="#ff6b35" />
        Učitavanje stranice...
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <SidebarToggle $collapsed={sidebarCollapsed} onClick={toggleSidebar}>
        {!sidebarCollapsed ? <FiMenu /> : <IoClose />}
      </SidebarToggle>

      {/* Add the overlay */}
      <SidebarOverlay $isOpen={sidebarCollapsed} />

      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarHeader>
          <LogoIcon />
          <SidebarSubtitle>Bend Panel</SidebarSubtitle>
        </SidebarHeader>

        <SidebarNav>
          {navItems.map((item) => {
            const showAlert =
              (item.id === "products" && hasLowStockAlerts) ||
              (item.id === "orders" && hasPendingOrders);

            return (
              <NavItem
                key={item.id}
                $active={pathname === item.to}
                onClick={() => changePage(item.to)}
              >
                <NavIcon>
                  <item.icon />
                  {showAlert && <AlertDot />}
                </NavIcon>
                <NavText>{item.label}</NavText>
              </NavItem>
            );
          })}
          <BottomNav>
            <NavSupport onClick={handleOpenSupportChat}>
              <NavIcon>
                <BiSupport />
              </NavIcon>
              Korisnička podrška
            </NavSupport>
            <NavLogout onClick={handleLogout}>
              <NavIcon>
                <FiLogOut />
              </NavIcon>
              Odjavi se
            </NavLogout>
          </BottomNav>
        </SidebarNav>
      </Sidebar>

      {!hideContent && (
        <MainContent $expanded={sidebarCollapsed}>
          <Header>
            <HeaderActions>
              <VerificationStatusBadge
                loading={verificationStatus.loading}
                user={user}
                setEmailVerificationModal={setEmailVerificationModal}
                emailVerificationModal={emailVerificationModal}
                status={getVerificationStatus(verificationData)}
                onClick={
                  verificationData?.emailVerified
                    ? null
                    : handleOpenVerificationModal
                }
                showTooltip={false}
                size="big"
              />

              <UserInfo
                onClick={() => {
                  navigate("/bend-panel/podesavanja");
                  setTimeout(() => {
                    window.scrollTo(0, 0);
                  }, 300);
                }}
              >
                <span>@{user.username}</span>
                <FiUser size={18} />
              </UserInfo>
            </HeaderActions>
          </Header>
          <Content>{children}</Content>
        </MainContent>
      )}

      {/* Email Verification */}
      <EmailVerificationModal
        isOpen={emailVerificationModal}
        onClose={handleCloseVerificationModal}
        currentUser={user}
      />

      {/* Support Chat */}
      <SupportChat
        isOpen={supportChatOpen}
        onClose={handleCloseSupportChat}
        onMinimize={handleMinimizeSupportChat}
        isMinimized={supportChatMinimized}
      />
    </Container>
  );
};

export default BandLayout;
